import { zodResolver } from "@hookform/resolvers/zod";
import React, { type ReactNode, useCallback } from "react";
import { useForm } from "react-hook-form";
import { Link, useLocation } from "react-router-dom";
import { logout } from "src/auth/mutations";
import PasswordInput from "src/components/PasswordInput";
import TextInput, { Variant } from "src/components/TextInput";
import UnauthenticatedLayout from "src/components/UnauthenticatedLayout";
import { Button } from "src/components/ui/button";
import { RegularText } from "src/components/v1";
import config from "src/config";
import { useFormReducer } from "src/hooks";
import { registerCustomInput } from "src/utilities/registerCustomInput";
import { cn } from "src/utilities/shadcnUtils";
import { z } from "zod";

export interface RegisterLayoutProps {
  onSubmit?: (submission?: Record<string, string>) => void;
  userEmail?: string;
  isLoading?: boolean;
  className?: string;
  hideHelperButtons?: boolean;
}

const LOGIN = z
  .object({
    password: z.string().min(8),
    confirm: z.string(),
  })
  .refine((data) => data.password === data.confirm, {
    path: ["confirm"],
  });

const HelperText = ({ children, error }: { children: ReactNode; error?: boolean }) => (
  <RegularText className={cn("text-sm text-gray-500 mb-1", error && "text-red-500")}>{children}</RegularText>
);

const RegisterLayout = (props: RegisterLayoutProps) => {
  return (
    <UnauthenticatedLayout
      header={
        <>
          Welcome to {config.brandName}!
          <br />
          Register your email to continue
        </>
      }
    >
      <RegisterInnerLayout {...props} />
    </UnauthenticatedLayout>
  );
};

export const RegisterInnerLayout = ({
  userEmail,
  isLoading,
  className,
  hideHelperButtons,
  ...props
}: RegisterLayoutProps) => {
  const { formValues, handleTextChange } = useFormReducer({
    initialValues: {
      password: "",
      confirm: "",
    },
  });

  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm({
    resolver: zodResolver(LOGIN),
  });

  const onSubmit = useCallback(() => {
    props.onSubmit?.({ password: formValues.password });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.onSubmit, formValues.password]);

  const location = useLocation();
  const onClickNeverMind = useCallback(() => {
    logout().then(() => {
      window.location.href = location?.state?.from || "/";
    });
  }, []);

  return (
    <form className={className} onSubmit={handleSubmit(onSubmit)}>
      <div>
        <TextInput
          clearable={false}
          className="w-full"
          defaultValue={userEmail}
          disabled
          variant={Variant.V2}
        />
        <PasswordInput
          className="w-full"
          autoComplete="new-password"
          defaultValue={formValues.password}
          {...registerCustomInput(register("password"), handleTextChange)}
        />
        <PasswordInput
          className="w-full"
          label="Confirm Password"
          autoComplete="new-password"
          defaultValue={formValues.confirm}
          {...registerCustomInput(register("confirm"), handleTextChange)}
        />
      </div>
      <HelperText error={errors?.password?.type === "too_small"}>
        Password must be more than 8 characters long.
      </HelperText>
      <div className="flex flex-col items-center pb-8">
        <Button
          type="submit"
          className="rounded-full w-full max-w-[200px] mt-4"
          variant="green"
          pending={isLoading}
        >
          Register
        </Button>
        {!hideHelperButtons ? (
          <>
            <Link to="/login" className="no-underline">
              <Button className="text-muted-foreground mt-1" variant="ghost">
                Already have an account?
              </Button>
            </Link>
            <Button className="text-muted-foreground mt-1" onClick={onClickNeverMind} variant="ghost">
              Never mind
            </Button>
          </>
        ) : null}
      </div>
    </form>
  );
};

export default React.memo(RegisterLayout);
